import { IProjectAccount } from '../models/project-account.model';
import { IStatementLayout, StatementNode } from '../models/statement-layout.model';
import { displaySignOf, toUnits } from './accounts-presentation';

/**
 * One period column's figure for an account: minor units in the LEDGER sign (debit − credit), or
 * `null` when the account has no figure for that period.
 */
export type PeriodPick = (account: IProjectAccount) => number | null;

/** Per node id, one value per period, in displayed units and sign. */
export type StatementValues = ReadonlyMap<string, readonly (number | null)[]>;

const NOTHING = 0;

/**
 * Every node of a statement layout, evaluated per period.
 *
 * - POSITION: the sum of its accounts.
 * - GROUP: the sum of its children, subtotals left out.
 * - SUBTOTAL: the running sum of the siblings before it, earlier subtotals left out (otherwise
 *   Bruttogewinn would count Betriebsertrag twice). The annual report's multi-year comparison
 *   computes its subtotals the same way.
 *
 * Every amount in the display sign (`displaySignOf`), so the rows add up as they are read. Summed in
 * minor units and converted once, so a column of Rappen does not drift. A node is `null` in a period
 * where none of its accounts has a figure: an absence, not a zero.
 *
 * Pass ALL accounts of the statement: a search or a chip hides rows, never part of a sum.
 */
export function evaluateStatement(
    layout: IStatementLayout,
    accounts: readonly IProjectAccount[],
    periods: readonly PeriodPick[],
): StatementValues {
    const byPosition = new Map<string, IProjectAccount[]>();
    for (const account of accounts) {
        if (account.accountGroup === null) {
            continue;
        }
        const bucket = byPosition.get(account.accountGroup);
        if (bucket === undefined) {
            byPosition.set(account.accountGroup, [account]);
        } else {
            bucket.push(account);
        }
    }

    // Minor units until the end; converted in one pass below.
    const minor = new Map<string, (number | null)[]>();

    const evaluate = (node: Exclude<StatementNode, { kind: 'SUBTOTAL' }>): (number | null)[] => {
        const values =
            node.kind === 'POSITION'
                ? periods.map((pick) => sumOf((byPosition.get(node.position) ?? []).map((a) => signedMinor(a, pick))))
                : evaluateChildren(node.children);
        minor.set(node.id, values);
        return values;
    };

    // A parent's own value, and every subtotal among its children on the way.
    const evaluateChildren = (children: readonly StatementNode[]): (number | null)[] => {
        const counted: (number | null)[][] = [];
        for (const child of children) {
            if (child.kind === 'SUBTOTAL') {
                minor.set(
                    child.id,
                    periods.map((_pick, index) => sumOf(counted.map((values) => values[index] ?? null))),
                );
            } else {
                counted.push(evaluate(child));
            }
        }
        return periods.map((_pick, index) => sumOf(counted.map((values) => values[index] ?? null)));
    };

    evaluateChildren(layout.roots);

    const result = new Map<string, readonly (number | null)[]>();
    minor.forEach((values, id) =>
        result.set(
            id,
            values.map((value) => (value === null ? null : toUnits(value))),
        ),
    );
    return result;
}

/** An account's figure per period in displayed units and sign — the same numbers its row shows. */
export function displayValuesOf(account: IProjectAccount, periods: readonly PeriodPick[]): (number | null)[] {
    return periods.map((pick) => {
        const value = signedMinor(account, pick);
        return value === null ? null : toUnits(value);
    });
}

/** The sum of some accounts per period, in displayed units and sign — for a bucket outside the layout. */
export function sumOfAccounts(accounts: readonly IProjectAccount[], periods: readonly PeriodPick[]): (number | null)[] {
    return periods.map((pick) => {
        const value = sumOf(accounts.map((account) => signedMinor(account, pick)));
        return value === null ? null : toUnits(value);
    });
}

/** An account's figure in minor units and the display sign, never `-0`. */
function signedMinor(account: IProjectAccount, pick: PeriodPick): number | null {
    const value = pick(account);
    if (value === null) {
        return null;
    }
    return value === NOTHING ? NOTHING : displaySignOf(account.accountGroup, account.accountNumber) * value;
}

function sumOf(values: readonly (number | null)[]): number | null {
    const present = values.filter((value): value is number => value !== null);
    return present.length === NOTHING ? null : present.reduce((sum, value) => sum + value, NOTHING);
}
